import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useObligaciones, ObligacionAmbiental } from "@/hooks/useObligaciones";
import { Plus, Save, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useFormPersistence } from "@/hooks/useStatePersistence";
import { useAuth } from "@/hooks/useAuth";

interface NuevaObligacionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

const NuevaObligacionModal = ({ isOpen, onClose, onSave }: NuevaObligacionModalProps) => {
  const { createObligacion } = useObligaciones();
  const { toast } = useToast();
  const { user } = useAuth();
  
  const { values: formData, updateField, resetForm } = useFormPersistence(
    'nueva_obligacion',
    {
      codigo: "",
      nombre_programa: "",
      tipo: "" as 'Ambiental' | 'Social' | 'Biótico' | 'Abiótico' | 'SST' | "",
      objetivo: "",
      frecuencia: "" as 'Continuo' | 'Mensual' | 'Trimestral' | 'Anual' | 'Única' | "",
      entidad_reporte: "",
      responsable_interno: "",
      estado: "Pendiente" as 'Pendiente' | 'En Proceso' | 'Cumplido' | 'Vencido',
      vencimiento: "",
      nivel_cumplimiento: 0,
      plazo_cumplimiento: "",
      observaciones: ""
    },
    user?.id
  );

  const handleInputChange = (field: string, value: string | number) => {
    updateField(field as keyof typeof formData, value);
  };


  const handleSave = async () => {
    // Validaciones básicas
    if (!formData.codigo || !formData.nombre_programa || !formData.tipo || !formData.frecuencia) {
      toast({
        title: "Error",
        description: "Por favor completa los campos obligatorios (Código, Nombre del Programa, Tipo, Frecuencia)",
        variant: "destructive",
      });
      return;
    }

    try {
      await createObligacion({
        codigo: formData.codigo,
        nombre_programa: formData.nombre_programa,
        tipo: formData.tipo as 'Ambiental' | 'Social' | 'Biótico' | 'Abiótico' | 'SST',
        objetivo: formData.objetivo,
        frecuencia: formData.frecuencia as 'Continuo' | 'Mensual' | 'Trimestral' | 'Anual' | 'Única',
        entidad_reporte: formData.entidad_reporte,
        responsable_interno: formData.responsable_interno,
        estado: formData.estado,
        vencimiento: formData.vencimiento,
        indicadores: [],
        nivel_cumplimiento: formData.nivel_cumplimiento,
        plazo_cumplimiento: formData.plazo_cumplimiento,
        observaciones: formData.observaciones,
        evidencia: ""
      });

      // Resetear formulario
      resetForm();

      onSave();
      onClose();
    } catch (error) {
      // El error se maneja en el hook
    }
  };

  const responsablesComunes = [
    "Ing. Ambiental",
    "Supervisor Ambiental",
    "Coordinador Ambiental", 
    "Gestor de Residuos",
    "Coordinador Social",
    "Biólogo",
    "Jefe de Recursos Humanos"
  ];

  const entidadesComunes = [
    "ANLA",
    "CORNARE",
    "Secretaría de Ambiente",
    "Alcaldía Municipal",
    "SENA"
  ];

  const programasComunes = [
    "Manejo de Aguas",
    "Manejo del Suelo",
    "Calidad del Aire",
    "Gestión de Residuos",
    "Control de Ruido",
    "Participación Social",
    "Biodiversidad",
    "Desarrollo Local"
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Nueva Obligación
          </DialogTitle>
          <DialogDescription>
            Completa la información para crear una nueva obligación
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 py-4">
          {/* Código y Nombre del Programa */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="codigo">Código *</Label>
              <Input
                id="codigo"
                placeholder="Ej: PMA-10"
                value={formData.codigo}
                onChange={(e) => handleInputChange('codigo', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="nombre_programa">Nombre del Programa *</Label>
              <Input
                id="nombre_programa"
                placeholder="Escribir nombre del programa..."
                value={formData.nombre_programa}
                onChange={(e) => handleInputChange('nombre_programa', e.target.value)}
                className="mt-1"
              />
            </div>
          </div>

          {/* Tipo y Objetivo */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="tipo">Tipo *</Label>
              <Select value={formData.tipo} onValueChange={(value) => handleInputChange('tipo', value)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleccionar tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Ambiental">🌍 Ambiental</SelectItem>
                  <SelectItem value="Social">👥 Social</SelectItem>
                  <SelectItem value="Biótico">🌱 Biótico</SelectItem>
                  <SelectItem value="Abiótico">🪨 Abiótico</SelectItem>
                  <SelectItem value="SST">🦺 SST</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="objetivo">Objetivo</Label>
              <Input
                id="objetivo"
                placeholder="Descripción del objetivo..."
                value={formData.objetivo}
                onChange={(e) => handleInputChange('objetivo', e.target.value)}
                className="mt-1"
              />
            </div>
          </div>

          {/* Frecuencia y Entidad de Reporte */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="frecuencia">Frecuencia *</Label>
              <Select value={formData.frecuencia} onValueChange={(value) => handleInputChange('frecuencia', value)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleccionar frecuencia" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Continuo">🔄 Continuo</SelectItem>
                  <SelectItem value="Mensual">📅 Mensual</SelectItem>
                  <SelectItem value="Trimestral">📋 Trimestral</SelectItem>
                  <SelectItem value="Anual">📊 Anual</SelectItem>
                  <SelectItem value="Única">⚡ Única</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="entidad">Entidad de Reporte</Label>
              <Select value={formData.entidad_reporte} onValueChange={(value) => handleInputChange('entidad_reporte', value)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleccionar entidad" />
                </SelectTrigger>
                <SelectContent>
                  {entidadesComunes.map((entidad) => (
                    <SelectItem key={entidad} value={entidad}>{entidad}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Responsable Interno y Estado */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="responsable">Responsable Interno</Label>
              <Select value={formData.responsable_interno} onValueChange={(value) => handleInputChange('responsable_interno', value)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleccionar responsable" />
                </SelectTrigger>
                <SelectContent>
                  {responsablesComunes.map((responsable) => (
                    <SelectItem key={responsable} value={responsable}>{responsable}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="estado">Estado</Label>
              <Select value={formData.estado} onValueChange={(value) => handleInputChange('estado', value)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleccionar estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pendiente">⏳ Pendiente</SelectItem>
                  <SelectItem value="En Proceso">🔄 En Proceso</SelectItem>
                  <SelectItem value="Cumplido">✅ Cumplido</SelectItem>
                  <SelectItem value="Vencido">❌ Vencido</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Vencimiento y Nivel de Cumplimiento */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="vencimiento">Vencimiento</Label>
              <Input
                id="vencimiento"
                type="date"
                value={formData.vencimiento}
                onChange={(e) => handleInputChange('vencimiento', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="nivel_cumplimiento">Nivel de Cumplimiento (%)</Label>
              <Input
                id="nivel_cumplimiento"
                type="number"
                min="0"
                max="100"
                placeholder="0"
                value={formData.nivel_cumplimiento}
                onChange={(e) => handleInputChange('nivel_cumplimiento', parseInt(e.target.value) || 0)}
                className="mt-1"
              />
            </div>
          </div>

          {/* Plazo de Cumplimiento */}
          <div>
            <Label htmlFor="plazo">Plazo de Cumplimiento</Label>
            <Select value={formData.plazo_cumplimiento} onValueChange={(value) => handleInputChange('plazo_cumplimiento', value)}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Seleccionar plazo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Mensual">📅 Mensual</SelectItem>
                <SelectItem value="Trimestral">📋 Trimestral</SelectItem>
                <SelectItem value="Semestral">📊 Semestral</SelectItem>
                <SelectItem value="Anual">📈 Anual</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Observaciones */}
          <div>
            <Label htmlFor="observaciones">Observaciones</Label>
            <Textarea
              id="observaciones"
              placeholder="Observaciones adicionales..."
              value={formData.observaciones}
              onChange={(e) => handleInputChange('observaciones', e.target.value)}
              className="mt-1"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            <X className="w-4 h-4 mr-2" />
            Cancelar
          </Button>
          <Button onClick={handleSave} className="bg-environmental hover:bg-environmental-dark">
            <Save className="w-4 h-4 mr-2" />
            Crear Obligación
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NuevaObligacionModal;